import { render, html, LitElement, css } from 'lit';
import { property } from 'lit/decorators.js';
import 'lit/directive.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { curry } from '@typed/curry';
import eagerDash from '@lavadrop/kebab-case';
import eagerCamel from '@lavadrop/camel-case';
import { memoize } from '@pacote/memoize';

const configurable = true;
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
function bound(_, key, descriptor) {
    if (typeof (descriptor === null || descriptor === void 0 ? void 0 : descriptor.value) !== 'function')
        throw new TypeError(`Only methods can be decorated with @bound. <${key !== null && key !== void 0 ? key : _.name}> is not a method!`); /* c8 ignore next */
    return {
        configurable,
        get() {
            const value = descriptor.value.bind(this);
            Object.defineProperty(this, key, { value, configurable, writable: true });
            return value;
        },
    };
}

/**
 * Returns the event name for the given property.
 * @param  {string}                       name    property name
 * @param  {PropertyDeclaration} options property declaration
 * @return                                event name to fire
 */
function eventNameForProperty(name, { notify, attribute } = {}) {
    if (notify && typeof notify === 'string') {
        return notify;
    } else if (attribute && typeof attribute === 'string') {
        return `${attribute}-changed`;
    } else {
        return `${name.toLowerCase()}-changed`;
    }
}

// eslint-disable-next-line valid-jsdoc
/**
 * Enables the nofity option for properties to fire change notification events
 *
 * @template TBase
 * @param {Constructor<TBase>} baseElement
 */
const LitNotify = (baseElement) => class NotifyingElement extends baseElement {
    /**
     * check for changed properties with notify option and fire the events
     */
    update(changedProps) {
        super.update(changedProps);

        for (const prop of changedProps.keys()) {
            const declaration = this.constructor.elementProperties.get(prop);
            if (!declaration || !declaration.notify) continue;
            const type = eventNameForProperty(prop, declaration);
            const value = this[prop];
            this.dispatchEvent(new CustomEvent(type, { detail: { value } }));
        }
    }
};

/**
 * Enables the nofity option for properties to fire change notification events
 */
function ReadOnlyPropertiesMixin(superclass) {
  const _readOnlyPropertyNamesMap = new Map();

  return class ReadOnlyPropertiesClass extends superclass {
    constructor() {
      super();
      this._readOnlyPropertyInitializedMap = new Map();
    }

    static createProperty(name, options) {
      let finalOptions = options;

      if (options.readOnly) {
        const privateName = Symbol(name);

        _readOnlyPropertyNamesMap.set(name, privateName);

        Object.defineProperty(this.prototype, name, {
          get() {
            return this[privateName];
          },

          set(value) {
            // allow for class field initialization
            /* istanbul ignore if */
            if (this._readOnlyPropertyInitializedMap.get(name)) return;
            this[privateName] = value;
            this._readOnlyPropertyInitializedMap.set(name, true);
          },
        });

        finalOptions = { ...options, noAccessor: true };
      }
      // It seems like there's really no good way to extend a class and its
      // static members in typescript
      // @ts-ignore
      super.createProperty(name, finalOptions);
    }

    /**
     * Set read-only properties
     */
    async setReadOnlyProperties(props) {
      await Promise.all(
        Object.entries(props).map(([name, newVal]) => {
          const privateName = _readOnlyPropertyNamesMap.get(name);
          const oldVal = this[privateName];
          this[privateName] = newVal;
          return this.requestUpdate(name, oldVal);
        }),
      );
    }
  };
}

/**
 * Remove an element from the DOM
 * @param  el element to remove
 */
/* istanbul ignore next */
const remove = (el) => el === null || el === void 0 ? void 0 : el.remove();
const appendTemplate = curry(function appendTemplate(template, target) {
    const tmp = document.createElement('div');
    render(template, tmp);
    const { firstElementChild } = tmp;
    target.appendChild(firstElementChild);
    tmp.remove();
    return firstElementChild;
});

const identity = x => x;
/** camelCase a string */
memoize(identity, eagerCamel);
/** dash-case a string */
const dash = memoize(identity, eagerDash);
/**
 * Generates a random mount point for Stripe Elements. This will allow multiple
 * Elements forms to be embedded on a single page.
 * @param {'STRIPE-ELEMENTS'|'STRIPE-PAYMENT-REQUEST'} tagName element tag name
 * @return {string} mount element id
 */
function generateRandomMountElementId(tagName) {
    return `${tagName.toLowerCase()}-mount-point-${(Date.now() + (Math.random() * 1000)).toString(36).substr(0, 8)}`;
}

const elem = xs => x => xs.includes(x);
const isRepresentation = elem(['paymentMethod', 'source', 'token']);

/**
 * Throws an error if the response is not OK.)
 */
async function throwBadResponse(response) {
    const { ok, statusText } = response;
    if (!ok)
        throw new Error(statusText);
    return response;
}

var __decorate = (undefined && undefined.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (undefined && undefined.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
class StripeElementsError extends Error {
    constructor(tag, message) {
        super(`<${tag}>: ${message}`);
        this.originalMessage = message;
    }
}
function isStripeElementsError(error) {
    return !!error && error instanceof StripeElementsError;
}
/* istanbul ignore next */
const removeAllMounts = (slotName) => (host) => host.querySelectorAll(`[slot="${slotName}"][name="${slotName}"]`)
    .forEach(remove);
const slotTemplate = (slotName) => html `<slot slot="${slotName}" name="${slotName}"></slot>`;
const errorConverter = {
    toAttribute: (error) => !error ? null
        : isStripeElementsError(error) ? error.originalMessage
            : error.message || '',
};
/**
 * @fires 'error' - The validation error, or the error returned from stripe.com
 * @fires 'payment-method' - The PaymentMethod received from stripe.com
 * @fires 'source' - The Source received from stripe.com
 * @fires 'token' - The Token received from stripe.com
 * @fires 'success' - When a payment succeeds
 * @fires 'ready' - Stripe has been initialized and mounted
 *
 * @fires 'stripe-ready' - **DEPRECATED**. Will be removed in a future major version. Use `ready` instead
 * @fires 'stripe-error' - **DEPRECATED**. Will be removed in a future major version. Use `error` instead
 * @fires 'stripe-payment-method' - **DEPRECATED**. Will be removed in a future major version. Use `payment-method` instead
 * @fires 'stripe-source' - **DEPRECATED**. Will be removed in a future major version. Use `source` instead
 * @fires 'stripe-token' - **DEPRECATED**. Will be removed in a future major version. Use `token` instead
 *
 * @csspart 'error' - container for the error message
 * @csspart 'stripe' - container for the stripe element
 */
class StripeBase extends ReadOnlyPropertiesMixin(LitNotify(LitElement)) {
    constructor() {
        super(...arguments);
        /**
         * Type of payment representation to generate.
         */
        this.generate = 'source';
        /** Whether to display the error message */
        this.showError = false;
        /* READ-ONLY FIELDS */
        /* PAYMENT REPRESENTATIONS */
        /**
         * Stripe PaymentMethod
         */
        this.paymentMethod = null;
        /**
         * Stripe Source
         */
        this.source = null;
        /**
         * Stripe Token
         */
        this.token = null;
        /**
         * Stripe element instance
         */
        this.element = null;
        /**
         * Stripe Elements instance
         */
        this.elements = null;
        /**
         * Stripe or validation error
         */
        this.error = null;
        /**
         * If the element is focused.
         */
        this.focused = false;
        /**
         * Whether the stripe element is ready to receive focus.
         */
        this.ready = false;
        /**
         * Stripe instance
         */
        this.stripe = null;
        // DEPRECATED
        /**
         * Whether the element has an error
         * **DEPRECATED**. Will be removed in a future version. Use `error` instead
         * @deprecated
         */
        this.hasError = false;
        /**
         * Whether the stripe element is ready to receive focus.
         * **DEPRECATED**. Will be removed in a future version. use `ready` instead.
         * @deprecated
         */
        this.stripeReady = false;
        /* PRIVATE FIELDS */
        /**
         * Breadcrumbs back up to the document.
         */
        this.shadowHosts = [];
    }
    /**
     * Stripe.js mount point element. Due to limitations in the Stripe.js library, this element must be connected to the document.
     */
    get stripeMount() { return document.getElementById(this.stripeMountId); }
    /* LIFECYCLE */
    /** @inheritdoc */
    render() {
        const { error, showError, slotName } = this;
        const errorMessage = isStripeElementsError(error) ? error.originalMessage : error === null || error === void 0 ? void 0 : error.message;
        return html `
      <div id="stripe" part="stripe">
        <slot id="stripe-slot" name="${slotName}"></slot>
      </div>

      <output id="error"
          for="stripe"
          part="error"
          ?hidden="${!showError}">
        ${ifDefined(errorMessage)}
      </output>
    `;
    }
    /** @inheritdoc */
    firstUpdated() {
        this.destroyMountPoints();
        this.initMountPoints();
    }
    /** @inheritdoc */
    updated(changed) {
        var _a;
        /* istanbul ignore next */
        (_a = super.updated) === null || _a === void 0 ? void 0 : _a.call(this, changed);
        if (changed.has('error'))
            this.errorChanged();
        if (changed.has('publishableKey'))
            this.init();
        [...changed.keys()].forEach(this.representationChanged);
    }
    /** @inheritdoc */
    async disconnectedCallback() {
        var _a;
        super.disconnectedCallback();
        /* istanbul ignore next */
        await ((_a = this.unmount) === null || _a === void 0 ? void 0 : _a.call(this));
        this.destroyMountPoints();
    }
    /* PUBLIC API */
    /**
     * Resets and clears the stripe element.
     */
    reset() {
        var _a, _b;
        /* istanbul ignore next */
        (_b = (_a = this.element) === null || _a === void 0 ? void 0 : _a.clear) === null || _b === void 0 ? void 0 : _b.call(_a);
        this.resetRepresentations();
        this.setReadOnlyProperties({ error: null });
    }
    /** Blurs the element. */
    blur() {
        var _a;
        /* istanbul ignore next */
        (_a = this.element) === null || _a === void 0 ? void 0 : _a.blur();
    }
    /** Focuses the element. */
    focus() {
        var _a;
        /* istanbul ignore next */
        (_a = this.element) === null || _a === void 0 ? void 0 : _a.focus();
    }
    /* PRIVATE API */
    /**
     * Creates a new StripeElementsError
     */
    createError(message) {
        return new StripeElementsError(this.constructor.is, message);
    }
    /**
     * Clears the Payment Representation and fires an error event
     */
    async errorChanged() {
        // DEPRECATED
        const hasError = !!this.error;
        await this.setReadOnlyProperties({ hasError });
        // END DEPRECATED
        this.resetRepresentations();
        this.fireError(this.error);
    }
    /**
     * Fires an event.
     * @param type      event type
     * @param detail    detail value
     * @param [opts={}]
     */
    fire(type, detail, opts) {
        this.dispatchEvent(new CustomEvent(type, { detail, ...opts }));
    }
    /**
     * Fires an Error Event
     */
    fireError(error) {
        this.dispatchEvent(new ErrorEvent('error', { error }));
        // DEPRECATED
        this.dispatchEvent(new ErrorEvent('stripe-error', { bubbles: true, error }));
    }
    /**
     * Gets a CSS Custom Property value, respecting ShadyCSS.
     * @param   propertyName    CSS Custom Property
     * @param   precomputedStyle pre-computed style declaration
     */
    getCSSCustomPropertyValue(propertyName, precomputedStyle) {
        if (window.ShadyCSS)
            return window.ShadyCSS.getComputedStyleValue(this, propertyName);
        else
            return precomputedStyle.getPropertyValue(propertyName);
    }
    /**
     * Sets the token or error from the response.
     * @param   response       Stripe Response
     * @return
     */
    async handleResponse(response) {
        const { error = null, paymentMethod = null, source = null, token = null } = { ...response };
        await this.setReadOnlyProperties({ error, paymentMethod, source, token });
        if (error)
            throw error;
        else
            return response;
    }
    /**
     * Removes all mount points from the DOM
     */
    destroyMountPoints() {
        this.shadowHosts.forEach(removeAllMounts(this.slotName));
        if (this.stripeMount)
            this.stripeMount.remove();
    }
    /** @abstract */
    /* istanbul ignore next */
    initElement() {
        'abstract';
    }
    /**
     * Reinitializes Stripe and mounts the element.
     */
    async init() {
        await this.unmount();
        await this.initStripe();
        await this.initElement();
        this.initElementListeners();
        this.destroyMountPoints();
        this.initMountPoints();
        this.mount();
    }
    /**
     * Adds `ready`, `focus`, and `blur` listeners to the Stripe Element
     */
    initElementListeners() {
        if (!this.element)
            return;
        this.element.addEventListener('ready', this.onReady);
        this.element.addEventListener('focus', this.onFocus);
        this.element.addEventListener('blur', this.onBlur);
    }
    /**
     * Creates mount points for the Stripe Element
     */
    initMountPoints() {
        this.stripeMountId = generateRandomMountElementId(this.tagName);
        if (window.ShadyDOM)
            appendTemplate(this.mountPointTemplate(), this);
        else
            this.initShadowMountPoints();
    }
    /**
     * Prepares to mount Stripe Elements in light DOM.
     */
    initShadowMountPoints() {
        // trace each shadow boundary between us and the document
        let host = this;
        this.shadowHosts = [this];
        while (host = host.getRootNode().host) // eslint-disable-line easy-loops/easy-loops, no-cond-assign, prefer-destructuring
            this.shadowHosts.push(host);
        const { shadowHosts, slotName } = this;
        // Prepare the shallowest breadcrumb slot at document level
        const hosts = [...shadowHosts];
        const root = hosts.pop();
        if (!root.querySelector(`[slot="${slotName}"]`)) {
            const div = document.createElement('div');
            div.slot = slotName;
            root.appendChild(div);
        }
        const container = root.querySelector(`[slot="${slotName}"]`);
        // Render the form to the document, so that Stripe.js can mount
        appendTemplate(this.mountPointTemplate(), container);
        // Append breadcrumb slots to each shadowroot in turn,
        // from the document down to the <stripe-elements> instance.
        hosts.forEach(appendTemplate(slotTemplate(slotName)));
    }
    /**
     * Initializes Stripe and elements.
     */
    async initStripe() {
        const { publishableKey } = this;
        const stripe = (window.Stripe && publishableKey) ? Stripe(publishableKey) : null;
        const elements = stripe && stripe.elements();
        const error = stripe ? null : this.createError('requires Stripe.js to be loaded first.');
        if (error)
            console.warn(error.message); // eslint-disable-line no-console
        await this.setReadOnlyProperties({ elements, error, stripe });
    }
    /**
     * Mounts the Stripe Element
     */
    mount() {
        var _a;
        /* istanbul ignore next */
        if (!this.stripeMount)
            throw this.createError('Stripe Mount missing');
        (_a = this.element) === null || _a === void 0 ? void 0 : _a.mount(this.stripeMount);
    }
    mountPointTemplate() {
        const { stripeMountId, tagName } = this;
        return html `<div id="${ifDefined(stripeMountId)}" class="${tagName.toLowerCase()}-mount"></div>`;
    }
    /**
     * Unmounts and nullifies the card.
     */
    async unmount() {
        var _a, _b;
        (_b = (_a = this.element) === null || _a === void 0 ? void 0 : _a.unmount) === null || _b === void 0 ? void 0 : _b.call(_a);
        await this.setReadOnlyProperties({ element: null });
    }
    /**
     * Updates element state when Stripe Element is blurred.
     */
    async onBlur() {
        await this.setReadOnlyProperties({ focused: false });
    }
    /**
     * @param  {StripeFocusEvent} event
     * @private
     */
    async onFocus() {
        await this.setReadOnlyProperties({ focused: true });
    }
    /**
     * Sets the `ready` property when the stripe element is ready to receive focus.
     */
    async onReady(event) {
        await this.setReadOnlyProperties({ ready: true, stripeReady: true });
        this.fire('ready', event);
        // DEPRECATED
        this.fire('stripe-ready', event);
    }
    /**
     * POSTs the payment info represenation to the endpoint at `/action`
     */
    async postRepresentation() {
        const onError = (error) => this.setReadOnlyProperties({ error });
        const onSuccess = (success) => {
            this.fire('success', success);
            // DEPRECATED
            this.fire('stripe-payment-success', success);
        };
        const token = this.token || undefined;
        const source = this.source || undefined;
        const paymentMethod = this.paymentMethod || undefined;
        const body = JSON.stringify({ token, source, paymentMethod });
        const headers = { 'Content-Type': 'application/json' };
        const method = 'POST';
        return fetch(this.action, { body, headers, method })
            .then(throwBadResponse)
            .then(onSuccess)
            .catch(onError);
    }
    /**
     * Updates the state and fires events when the token, source, or payment method is updated.
     */
    representationChanged(name) {
        if (!isRepresentation(name))
            return;
        const value = this[name];
        /* istanbul ignore if */
        if (!value)
            return;
        // DEPRECATED
        this.fire(`stripe-${dash(name)}`, value);
        this.fire(`${dash(name)}`, value);
        if (this.action)
            this.postRepresentation();
    }
    /**
     * Resets the Payment Representations
     */
    resetRepresentations() {
        this.setReadOnlyProperties({
            paymentMethod: null,
            token: null,
            source: null,
        });
    }
}
__decorate([
    property({ type: String }),
    __metadata("design:type", String)
], StripeBase.prototype, "action", void 0);
__decorate([
    property({ type: String, attribute: 'client-secret' }),
    __metadata("design:type", String)
], StripeBase.prototype, "clientSecret", void 0);
__decorate([
    property({ type: String }),
    __metadata("design:type", String)
], StripeBase.prototype, "generate", void 0);
__decorate([
    property({ type: String, attribute: 'publishable-key', reflect: true, notify: true }),
    __metadata("design:type", String)
], StripeBase.prototype, "publishableKey", void 0);
__decorate([
    property({ type: Boolean, attribute: 'show-error', reflect: true }),
    __metadata("design:type", Object)
], StripeBase.prototype, "showError", void 0);
__decorate([
    property({ type: Object, notify: true, readOnly: true, attribute: 'payment-method' }),
    __metadata("design:type", Object)
], StripeBase.prototype, "paymentMethod", void 0);
__decorate([
    property({ type: Object, notify: true, readOnly: true }),
    __metadata("design:type", Object)
], StripeBase.prototype, "source", void 0);
__decorate([
    property({ type: Object, notify: true, readOnly: true }),
    __metadata("design:type", Object)
], StripeBase.prototype, "token", void 0);
__decorate([
    property({ type: Object, readOnly: true }),
    __metadata("design:type", Object)
], StripeBase.prototype, "element", void 0);
__decorate([
    property({ type: Object, readOnly: true }),
    __metadata("design:type", Object)
], StripeBase.prototype, "elements", void 0);
__decorate([
    property({
        type: Object,
        notify: true,
        readOnly: true,
        reflect: true,
        converter: errorConverter,
    }),
    __metadata("design:type", Object)
], StripeBase.prototype, "error", void 0);
__decorate([
    property({ type: Boolean, reflect: true, notify: true, readOnly: true }),
    __metadata("design:type", Object)
], StripeBase.prototype, "focused", void 0);
__decorate([
    property({ type: Boolean, reflect: true, notify: true, readOnly: true }),
    __metadata("design:type", Object)
], StripeBase.prototype, "ready", void 0);
__decorate([
    property({ type: Object, readOnly: true }),
    __metadata("design:type", Object)
], StripeBase.prototype, "stripe", void 0);
__decorate([
    property({ type: Boolean, attribute: 'has-error', reflect: true, notify: true, readOnly: true }),
    __metadata("design:type", Object)
], StripeBase.prototype, "hasError", void 0);
__decorate([
    property({
        type: Boolean,
        attribute: 'stripe-ready',
        reflect: true,
        notify: true,
        readOnly: true,
    }),
    __metadata("design:type", Object)
], StripeBase.prototype, "stripeReady", void 0);
__decorate([
    bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", Promise)
], StripeBase.prototype, "handleResponse", null);
__decorate([
    bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", Promise)
], StripeBase.prototype, "onBlur", null);
__decorate([
    bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", Promise)
], StripeBase.prototype, "onFocus", null);
__decorate([
    bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", Promise)
], StripeBase.prototype, "onReady", null);
__decorate([
    bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String]),
    __metadata("design:returntype", void 0)
], StripeBase.prototype, "representationChanged", null);

const styles = css`[hidden] {
  display: none !important;
}

:host {
  display: block;
  box-sizing: border-box;
}

#error {
  font-family: sans-serif;
  font-size: 14px;
  padding-left: 42px;
  padding-bottom: 10px;
}
`;

export { StripeBase as S, bound as b, dash as d, styles as s };
//# sourceMappingURL=shared.js.map
